import { stageToBlobAsync } from '@hts/shared/utils';
import Konva from 'konva';
import { useCallback, useMemo, useRef } from 'react';
import { Layer, Stage } from 'react-konva';
import { KonvaImage } from '../../components';
import { INewCardState } from '../../types';

interface IUseExportCroppedSecondaryImageProps {
  card: INewCardState | null;
}

export const useExportCroppedSecondaryImage = ({ card }: IUseExportCroppedSecondaryImageProps) => {
  const stageRef = useRef<Konva.Stage | null>(null);

  const cardDimensions = card?.imageDimensions;
  const secondaryImage = card?.secondaryImage;

  const getStageBlob = useCallback(async () => {
    if (!cardDimensions) return null;

    const stage = stageRef.current;

    const { width, height } = cardDimensions;

    if (!stage || !width || !height) return null;

    const stareWidth = stage.width();
    const stageHeight = stage.height();
    const pixelRatio = Math.max(width / stareWidth, height / stageHeight);

    return await stageToBlobAsync(stage, pixelRatio);
  }, [cardDimensions]);

  const renderExportCroppedSecondaryImage = useMemo(() => {
    if (
      secondaryImage &&
      cardDimensions?.width &&
      cardDimensions.height &&
      secondaryImage?.is_on_top === 0
    ) {
      const {
        currentCropWidth,
        currentCropHeight,
        currentCropX,
        currentCropY,
        width,
        height,
        rotation,
        x,
        y,
        currentImageUrl,
      } = secondaryImage;

      const containerWidth = 300;
      const containerHeight = (containerWidth * cardDimensions.height) / cardDimensions.width;
      const scaleX = containerWidth / cardDimensions.width;
      const scaleY = containerHeight / cardDimensions.height;

      return (
        <div
          style={{
            display: 'flex',
            visibility: 'hidden',
            position: 'fixed',
            zIndex: -1,
            width: containerWidth,
            height: containerWidth,
            top: 0,
            left: 0,
          }}
        >
          <Stage
            ref={stageRef}
            width={containerWidth}
            height={containerHeight}
            scaleX={scaleX}
            scaleY={scaleY}
          >
            <Layer>
              <KonvaImage
                imageUrl={currentImageUrl || ''}
                x={x}
                y={y}
                width={width}
                height={height}
                rotation={rotation}
                crop={{
                  height: currentCropHeight,
                  width: currentCropWidth,
                  x: currentCropX,
                  y: currentCropY,
                }}
                listening={false}
              />
            </Layer>
          </Stage>
        </div>
      );
    }

    return null;
  }, [cardDimensions?.height, cardDimensions?.width, secondaryImage]);

  return {
    renderExportCroppedSecondaryImage,
    getStageBlob,
  };
};
